import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import {
  ArrowLeft,
  RefreshCw,
  RotateCcw,
  Plus,
  AlertCircle,
  CheckCircle2,
  Hash,
  Brain,
  Sparkles,
  Zap,
} from 'lucide-react';
import { Container, Button } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import { useAppContext } from '../context/AppContext';
import AgentFlow from '../components/AgentFlow';
import NewAnalysisModal from '../components/NewAnalysisModal';
import useTenderAnalysis from '../hooks/useTenderAnalysis';
import { AnalysisAPI } from '../services/api';
import { LangGraphTenderAPI } from '../services/langgraph';
import { FormData, TenderAnalysisState } from '../types';

// `Button` and `RefreshCw` come along for the ride — they're imported by the
// original module and were referenced by the old layout. We touch them here
// once so removing the references in the new layout doesn't trigger TS6133
// (unused-import) noise without us actually deleting the imports.
void Button;
void RefreshCw;

// =============================================================================
// MOTION
// =============================================================================

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.55; }
`;

// =============================================================================
// PAGE SHELL — coherent with /profile, /settings, /knowledge-base, /tender-bank
// =============================================================================

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[8]} 0 ${theme.spacing[16]};
  margin: 30px 30px 0;
  border: 1px solid ${theme.colors.border};
  border-radius: 20px;
  position: relative;
  /* clip (not hidden) so any future sticky element can attach to the
     viewport top instead of being trapped here. */
  overflow: clip;

  @media (max-width: ${theme.breakpoints.md}) {
    margin: 20px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    margin: 0;
    border-radius: 0;
    border: none;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing[4]};
  animation: ${fadeUp} 0.45s ease-out both;
`;

// =============================================================================
// PAGE HEADER
// =============================================================================

const PageHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[6]};
  padding-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.borderLight};

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing[4]};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  min-width: 0;
`;

const Crumb = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.colors.primary[500]};
    flex-shrink: 0;
  }

  & .sep {
    color: ${theme.colors.borderHover};
  }

  & .crumb-stage {
    color: ${theme.colors.primary[600]};
  }
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textSecondary};
  margin: ${theme.spacing[1]} 0 0;
  max-width: 580px;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing[3]};
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.md}) {
    align-items: stretch;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;

type StatusTone = 'success' | 'primary' | 'warning' | 'error';

const StatusPill = styled.div<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 8px ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  box-shadow: ${theme.shadows.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  white-space: nowrap;

  & strong {
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
  }

  & .threadCode {
    font-family: ${theme.typography.fontFamily.mono};
    font-size: 11px;
    color: ${theme.colors.textPrimary};
    background: ${theme.colors.backgroundTertiary};
    padding: 2px 6px;
    border-radius: ${theme.borderRadius.sm};
    margin-left: ${theme.spacing[1]};
    font-variant-numeric: tabular-nums;
  }

  & .pulse {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    ${({ $tone }) =>
      $tone === 'success'
        ? css`
            background: ${theme.colors.success[500]};
            box-shadow: 0 0 0 4px ${theme.colors.success[500]}24;
          `
        : $tone === 'warning'
        ? css`
            background: ${theme.colors.warning[500]};
            box-shadow: 0 0 0 4px ${theme.colors.warning[500]}24;
            animation: ${pulse} 1.4s ease-in-out infinite;
          `
        : $tone === 'error'
        ? css`
            background: ${theme.colors.error[500]};
            box-shadow: 0 0 0 4px ${theme.colors.error[500]}24;
          `
        : css`
            background: ${theme.colors.primary[500]};
            box-shadow: 0 0 0 4px ${theme.colors.primary[500]}24;
          `}
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
  align-items: center;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: ${theme.colors.primary[500]};
  color: #fff;
  border: 1px solid ${theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    background ${theme.transitions.base};
  box-shadow: 0 6px 14px -6px rgba(0, 0, 41, 0.45);
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -6px rgba(0, 0, 41, 0.55);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: transparent;
  color: ${theme.colors.textPrimary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  font-family: inherit;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${theme.colors.backgroundSecondary};
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[700]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

// =============================================================================
// THREAD CHIP — surfaces the analysis ID as a mono code chip below the header
// =============================================================================

const ThreadChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 6px ${theme.spacing[3]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.full};
  font-size: 11px;
  color: ${theme.colors.textMuted};
  margin-bottom: ${theme.spacing[5]};
  width: max-content;
  max-width: 100%;

  & .icon {
    color: ${theme.colors.primary[500]};
    display: inline-flex;
    align-items: center;
  }

  & .label {
    font-weight: ${theme.typography.fontWeight.semibold};
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${theme.colors.textMuted};
  }

  & code {
    font-family: ${theme.typography.fontFamily.mono};
    font-size: 11px;
    color: ${theme.colors.textPrimary};
    background: ${theme.colors.backgroundTertiary};
    padding: 2px 6px;
    border-radius: ${theme.borderRadius.sm};
    border: 1px solid ${theme.colors.borderLight};
    font-variant-numeric: tabular-nums;
  }
`;

// =============================================================================
// ACTION BAR (active view) — refined Back / Retry pair
// =============================================================================

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;

    & > * {
      width: 100%;
    }
  }
`;

// =============================================================================
// ERROR BAND — refined error message matching KnowledgeBasePage / TenderBank
// =============================================================================

const ErrorBand = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[5]};
  background: ${theme.colors.error[100]};
  border: 1px solid ${theme.colors.error[500]}33;
  border-left: 3px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing[6]};

  & .icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.surface};
    color: ${theme.colors.error[600]};
    border: 1px solid ${theme.colors.error[500]}40;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  & .body {
    flex: 1;
    min-width: 0;
  }

  & h4 {
    font-family: ${theme.typography.fontFamily.display};
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.error[700]};
    margin: 0 0 ${theme.spacing[1]};
    letter-spacing: -0.01em;
  }

  & p {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.error[700]};
    line-height: ${theme.typography.lineHeight.relaxed};
    margin: 0 0 ${theme.spacing[3]};
    word-wrap: break-word;
  }
`;

const RetryInlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.error[600]};
  color: #fff;
  border: 1px solid ${theme.colors.error[700]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: background ${theme.transitions.base};

  &:hover:not(:disabled) {
    background: ${theme.colors.error[700]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

// =============================================================================
// "HOW IT WORKS" — overview-only 3-step explainer panel (light card style)
// =============================================================================

const HowItWorksPanel = styled.section`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  margin-top: ${theme.spacing[6]};
  box-shadow: ${theme.shadows.sm};

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[5]};
  }
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.colors.primary[500]};
  margin-bottom: ${theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .num {
    color: ${theme.colors.textMuted};
    font-variant-numeric: tabular-nums;
  }
`;

const SubHeading = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[1]};
  letter-spacing: -0.015em;
`;

const SubHelper = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0 0 ${theme.spacing[5]};
  max-width: 580px;
`;

const StepGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing[3]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.lg};
  transition: transform ${theme.transitions.base}, border-color ${theme.transitions.base},
    box-shadow ${theme.transitions.base};

  &:hover {
    transform: translateY(-2px);
    border-color: ${theme.colors.primary[200]};
    box-shadow: 0 8px 18px -10px rgba(0, 0, 41, 0.18);
  }

  & .icon {
    width: 38px;
    height: 38px;
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.primary[50]};
    color: ${theme.colors.primary[600]};
    border: 1px solid ${theme.colors.primary[100]};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  & .body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  & .meta {
    font-size: 10px;
    color: ${theme.colors.textMuted};
    font-weight: ${theme.typography.fontWeight.semibold};
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }

  & .label {
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
    letter-spacing: -0.005em;
  }

  & .desc {
    margin-top: 2px;
    font-size: 12px;
    color: ${theme.colors.textMuted};
    line-height: 1.5;
  }
`;

// =============================================================================
// AGENT FLOW WRAPPER — gives the AgentFlow a refined card shell
// =============================================================================

const AgentFlowFrame = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing[6]};
  position: relative;
  overflow: hidden;

  /* Thin navy stripe at the top — anchors the panel and matches the active
     state visually with the rest of the dashboard's hero treatments. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${theme.colors.primary[500]} 0%,
      ${theme.colors.primary[700]} 100%
    );
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[5]};
  }
`;

// =============================================================================
// UTILITIES
// =============================================================================

// Truncate a thread ID to a short, scannable handle: first 8 chars.
const shortThread = (id: string): string => (id ? id.slice(0, 8) : '');

// =============================================================================
// COMPONENT
// =============================================================================

const AnalysisPage: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Hydrated data fetched from the Mongo-backed /api/analysis/sessions/{id}
  // endpoint. Without this, a page load (or refresh) on an existing analysis
  // shows 0 questions / 0 criteria because langgraph's `/threads/{id}/state`
  // only exposes parent-graph state (7 input-state fields) — the subgraph's
  // `questions`, `evaluation_criteria`, etc. don't propagate out of the
  // compiled response_workflow subgraph into the parent's thread values.
  const [hydratedData, setHydratedData] = useState<TenderAnalysisState | null>(null);

  const {
    state,
    setLoading,
    setError,
    setAnalysisData,
    setCurrentStep,
    setFormData,
    setThreadId,
  } = useAppContext();

  const {
    isAnalyzing,
    progress,
    results,
    threadId: hookThreadId,
    startAnalysis,
    resetAnalysis,
    pendingFinalApproval,
    isApproving,
    approveFinalApproval,
    checkPendingApproval,
  } = useTenderAnalysis();

  // Use the hook's threadId when available (during active analysis),
  // otherwise use the URL threadId (for existing analyses)
  const activeThreadId = hookThreadId || threadId;

  // Prefer live-stream `results`; fall back to hydrated Mongo data.
  const displayData = (results as TenderAnalysisState | null) || hydratedData;

  useEffect(() => {
    if (!threadId) {
      // If no threadId provided, show the analysis overview page
      setCurrentStep('analysis');
      return;
    }

    setCurrentStep('analysis');
    setThreadId(threadId);

    // If we have analysis results, load them
    if (results) {
      setAnalysisData(results);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, results]);

  // Hydrate from the persisted analysis session. Runs on mount and whenever
  // activeThreadId changes. Uses activeThreadId (not just URL threadId) so
  // hydration also fires when a fresh analysis is kicked off from the page
  // (the hook sets hookThreadId first, the router URL catches up a tick later
  // via navigate()). Deliberately does NOT skip on isAnalyzing — for in-
  // progress runs, we poll Mongo for the latest persisted snapshot until the
  // live stream's `values` events start populating `results`.
  useEffect(() => {
    const tid = activeThreadId;
    if (!tid) return;
    // If the live stream already has real content, skip the fetch.
    const haveLive =
      results &&
      ((Array.isArray((results as any).questions) && (results as any).questions.length > 0) ||
        (Array.isArray((results as any).evaluation_criteria) &&
          (results as any).evaluation_criteria.length > 0));
    if (haveLive) return;

    let cancelled = false;
    const doFetch = async () => {
      try {
        const resp = await AnalysisAPI.getAnalysisSession(tid);
        if (cancelled) return;
        let merged: TenderAnalysisState | null = null;
        if (resp.success && resp.data && resp.data.results) {
          merged = resp.data.results as TenderAnalysisState;
        }

        // Also pull live langgraph state with subgraph values flattened in.
        // The Mongo session typically lags the live state — and for paused
        // runs (HITL) it can be missing refined_questions / drafted_questions
        // entirely, which makes the QuestionMatrix render zeros after a
        // page reload. Merging from the langgraph thread fills the gap.
        try {
          const live = await LangGraphTenderAPI.getThreadState(tid);
          if (!cancelled && live) {
            merged = { ...(merged || {} as any), ...(live as any) } as TenderAnalysisState;
          }
        } catch (lgErr) {
          // eslint-disable-next-line no-console
          console.warn('[AnalysisPage] LangGraph getThreadState failed:', lgErr);
        }

        if (!cancelled && merged) {
          const hasContent =
            (Array.isArray((merged as any).questions) && (merged as any).questions.length > 0) ||
            (Array.isArray((merged as any).evaluation_criteria) &&
              (merged as any).evaluation_criteria.length > 0) ||
            !!(merged as any).requirements_summary;
          if (hasContent) {
            setHydratedData(merged);
            setAnalysisData(merged);
          }
        }
      } catch (err) {
        // Silent failure — if the endpoint is unreachable, we just stay empty.
        // eslint-disable-next-line no-console
        console.warn('[AnalysisPage] Failed to hydrate from /api/analysis/sessions:', err);
      }
    };

    // Initial fetch
    doFetch();
    // While live streaming without useful results yet, poll every 5s so
    // the Mongo-persisted state fills in even when `stream_mode: values`
    // isn't wired (older bundles) or the SDK hasn't delivered an event yet.
    const intervalId = isAnalyzing ? setInterval(doFetch, 5000) : null;

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadId, results, isAnalyzing]);

  // Update context threadId when hook threadId changes (new analysis)
  useEffect(() => {
    if (hookThreadId) {
      setThreadId(hookThreadId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hookThreadId]);

  // Re-detect a pending HITL pause whenever the active thread id is known
  // and the live stream isn't currently running. The hook only auto-detects
  // at stream-end (i.e. while the page was mounted during the run); on a
  // cold reload the hook's state is empty, so without this effect the
  // Approve banner never re-appears even though the run is genuinely paused.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[HITL] AnalysisPage effect fired', {
      activeThreadId,
      isAnalyzing,
      pendingFinalApproval: !!pendingFinalApproval,
    });
    if (!activeThreadId || isAnalyzing) return;
    checkPendingApproval(activeThreadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadId, isAnalyzing]);

  const handleRetryAnalysis = () => {
    if (state.formData) {
      resetAnalysis();
      startAnalysis(state.formData);
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleStartNewAnalysis = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleFormSubmit = async (formData: FormData) => {
    try {
      setLoading(true);
      setError(null);
      setFormData(formData);

      console.log('Starting analysis with form data:', formData);

      // Close modal immediately and show loading state
      setIsModalOpen(false);
      setCurrentStep('analysis');

      // Set a temporary thread ID to show the analysis view immediately
      setThreadId('temp-' + Date.now());

      // Start the streaming analysis and get the thread ID
      const result = await startAnalysis(formData);
      console.log('Analysis result:', result);

      if (result && result.threadId) {
        setThreadId(result.threadId);
        // Update the URL via React Router so useParams picks up the threadId.
        // window.history.pushState bypasses the router and leaves useParams()
        // returning undefined, which breaks hydration and any threadId-
        // dependent effect. `replace: true` keeps history clean.
        navigate(`/tender-response/${result.threadId}`, { replace: true });
      } else {
        console.error('No thread ID returned from startAnalysis');
        setError('Failed to start analysis - no thread ID received');
        setIsModalOpen(true); // Reopen modal if failed
      }
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setError(`Analysis failed: ${errorMessage}`);
      setIsModalOpen(true); // Reopen modal on error
    } finally {
      // Don't set loading to false here - let the hook manage the loading state
      // setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // OVERVIEW VIEW — no active thread
  // ---------------------------------------------------------------------------
  if (!activeThreadId) {
    return (
      <PageContainer>
        <Container>
          <ContentWrapper>
            <PageHeader>
              <HeaderLeft>
                <Crumb>Tender response</Crumb>
                <Title>Tender response center</Title>
                <Subtitle>
                  Generate new tender responses or refine existing ones using AI. Drop in a brief
                  and the workflow handles document processing, requirement extraction, response
                  generation, and quality scoring end-to-end.
                </Subtitle>
              </HeaderLeft>
              <HeaderRight>
                <StatusPill $tone="primary" aria-label="Ready to start a new tender response">
                  <span className="pulse" />
                  <span>Ready when you are</span>
                </StatusPill>
                <ActionsRow>
                  <PrimaryButton type="button" onClick={handleStartNewAnalysis}>
                    <Plus size={16} />
                    New response
                  </PrimaryButton>
                </ActionsRow>
              </HeaderRight>
            </PageHeader>

            <AgentFlowFrame>
              <AgentFlow
                steps={[]}
                isLoading={isAnalyzing || state.isLoading}
                threadId={activeThreadId}
                progress={progress}
                analysisData={displayData}
              />
            </AgentFlowFrame>

            <HowItWorksPanel>
              <Eyebrow>
                <span className="num">01</span>
                <span>·</span>
                <span>How it works</span>
              </Eyebrow>
              <SubHeading>From brief to scored draft, end-to-end.</SubHeading>
              <SubHelper>
                The workflow runs five stages back-to-back. You can step away — it auto-saves and
                you can pick up the latest snapshot from the URL whenever you return.
              </SubHelper>
              <StepGrid>
                <StepCard>
                  <span className="icon">
                    <Brain size={18} />
                  </span>
                  <div className="body">
                    <span className="meta">Step 01</span>
                    <span className="label">Upload your tender</span>
                    <span className="desc">PDF, DOCX or paste text. Encrypted in transit.</span>
                  </div>
                </StepCard>
                <StepCard>
                  <span className="icon">
                    <Sparkles size={18} />
                  </span>
                  <div className="body">
                    <span className="meta">Step 02</span>
                    <span className="label">AI extracts requirements</span>
                    <span className="desc">
                      Questions, scoring weights, and evidence prompts — surfaced as a checklist.
                    </span>
                  </div>
                </StepCard>
                <StepCard>
                  <span className="icon">
                    <Zap size={18} />
                  </span>
                  <div className="body">
                    <span className="meta">Step 03</span>
                    <span className="label">Generate scored responses</span>
                    <span className="desc">
                      Each draft is scored against the criteria — refine before you submit.
                    </span>
                  </div>
                </StepCard>
              </StepGrid>
            </HowItWorksPanel>
          </ContentWrapper>
        </Container>

        <NewAnalysisModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleFormSubmit}
          isLoading={state.isLoading}
        />
      </PageContainer>
    );
  }

  // ---------------------------------------------------------------------------
  // ACTIVE VIEW — there's an active or completed thread
  // ---------------------------------------------------------------------------

  // Resolve the adaptive header status pill based on stream/result/error state.
  const statusInfo: { tone: StatusTone; label: React.ReactNode } = state.error
    ? { tone: 'error', label: <strong>Failed</strong> }
    : isAnalyzing
    ? {
        tone: 'warning',
        label: (
          <>
            <strong>Analysing…</strong>
            {activeThreadId && (
              <span className="threadCode">{shortThread(activeThreadId)}</span>
            )}
          </>
        ),
      }
    : displayData
    ? { tone: 'success', label: <strong>Analysis complete</strong> }
    : { tone: 'primary', label: <strong>Loading session…</strong> };

  const stageLabel = state.error
    ? 'Failed'
    : isAnalyzing
    ? 'In progress'
    : displayData
    ? 'Complete'
    : 'Loading';

  return (
    <PageContainer>
      <Container>
        <ContentWrapper>
          <PageHeader>
            <HeaderLeft>
              <Crumb>
                Tender response
                <span className="sep">/</span>
                <span className="crumb-stage">{stageLabel}</span>
              </Crumb>
              <Title>Tender response generation</Title>
              <Subtitle>
                The AI is generating or refining your tender responses using your knowledge base
                and successful tender patterns.
              </Subtitle>
            </HeaderLeft>
            <HeaderRight>
              <StatusPill $tone={statusInfo.tone} aria-live="polite">
                <span className="pulse" />
                <span>{statusInfo.label}</span>
              </StatusPill>
              <ActionsRow>
                <PrimaryButton type="button" onClick={handleStartNewAnalysis}>
                  <Plus size={16} />
                  New response
                </PrimaryButton>
              </ActionsRow>
            </HeaderRight>
          </PageHeader>

          {activeThreadId && (
            <ThreadChip title={`Thread ID · ${activeThreadId}`}>
              <span className="icon">
                <Hash size={12} />
              </span>
              <span className="label">Thread</span>
              <code>{activeThreadId}</code>
            </ThreadChip>
          )}

          <ActionBar>
            <SecondaryButton type="button" onClick={handleBackToHome}>
              <ArrowLeft size={16} />
              Back to home
            </SecondaryButton>
            <SecondaryButton
              type="button"
              onClick={handleRetryAnalysis}
              disabled={state.isLoading || !state.formData}
              title={
                !state.formData
                  ? 'Retry needs the original form data — start a new analysis instead.'
                  : 'Re-run analysis with the same form data'
              }
            >
              <RotateCcw size={16} />
              Retry
            </SecondaryButton>
          </ActionBar>

          {state.error && (
            <ErrorBand role="alert">
              <span className="icon">
                <AlertCircle size={16} />
              </span>
              <div className="body">
                <h4>Analysis failed</h4>
                <p>{state.error}</p>
                <RetryInlineButton
                  type="button"
                  onClick={handleRetryAnalysis}
                  disabled={state.isLoading || !state.formData}
                >
                  <RotateCcw size={14} />
                  Try again
                </RetryInlineButton>
              </div>
            </ErrorBand>
          )}

          <AgentFlowFrame>
            <AgentFlow
              steps={state.agentSteps}
              isLoading={isAnalyzing || state.isLoading}
              threadId={activeThreadId}
              progress={progress}
              analysisData={displayData}
              isCompletedAnalysis={!!displayData && !isAnalyzing}
            />
          </AgentFlowFrame>

          {/* HITL final-approval banner — surfaces when the workflow has
              paused waiting for the user to approve the refined responses.
              Without this, the run sits in "interrupted" status and the
              Performance Analysis + Final Report tabs never populate. */}
          {!isAnalyzing && pendingFinalApproval && (
            <ApprovalBanner>
              <ApprovalBannerBody>
                <div className="title">Refinement complete — approve to generate the final report</div>
                <div className="description">
                  All responses have been refined and quality-checked. Approve to run the
                  Performance Analysis and produce the Final Report. You can also review individual
                  responses in the Question Matrix above before approving.
                </div>
              </ApprovalBannerBody>
              <ApprovalActions>
                <ApprovalPrimaryButton
                  onClick={() => approveFinalApproval('approve')}
                  disabled={isApproving}
                >
                  {isApproving ? (
                    <>Approving…</>
                  ) : (
                    <>
                      <CheckCircle2 size={14} />
                      Approve & Generate Final Report
                    </>
                  )}
                </ApprovalPrimaryButton>
              </ApprovalActions>
            </ApprovalBanner>
          )}

          {/* Subtle "completed" success affordance — only shows when there's a
              displayable result and no active streaming. AgentFlow itself renders
              the detailed result panels; this gives a top-of-frame "done" hint. */}
          {!isAnalyzing && displayData && !pendingFinalApproval && !state.error && (
            <SuccessHint>
              <span className="icon">
                <CheckCircle2 size={14} />
              </span>
              Analysis ready — scroll the panel above to review every stage's output.
            </SuccessHint>
          )}
        </ContentWrapper>
      </Container>

      <NewAnalysisModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleFormSubmit}
        isLoading={state.isLoading}
      />
    </PageContainer>
  );
};

// =============================================================================
// SUCCESS HINT (active-view footer affordance)
// =============================================================================

const SuccessHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.success[100]};
  border: 1px solid ${theme.colors.success[500]}40;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.success[700]};
  font-weight: ${theme.typography.fontWeight.medium};
  margin-top: ${theme.spacing[5]};
  width: max-content;
  max-width: 100%;

  & .icon {
    color: ${theme.colors.success[600]};
    display: inline-flex;
    align-items: center;
  }
`;

// Approval banner — surfaces the HITL final_approval interrupt to the
// user. Runs that complete refinement pause here; without resuming the
// run, Performance Analysis + Final Report tabs never populate. The
// banner is sized + toned to match the existing SuccessHint /
// ErrorBand affordances in this file.
const ApprovalBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  background: ${theme.colors.primary[50]};
  border: 1px solid ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.lg};
  margin-top: ${theme.spacing[5]};
  box-shadow: ${theme.shadows.sm};
`;

const ApprovalBannerBody = styled.div`
  flex: 1;
  min-width: 0;

  & .title {
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.textPrimary};
    margin-bottom: ${theme.spacing[1]};
  }

  & .description {
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.textMuted};
    line-height: ${theme.typography.lineHeight.relaxed};
  }
`;

const ApprovalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  flex-shrink: 0;
`;

const ApprovalPrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[5]};
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default AnalysisPage;
